import {
	useEligibleForJsmLandUseCaseOnboarding,
	useEligibleForTwcg1935CodingAgentChoice,
	useIsDesktop,
	useLoggedInNewUser,
	useLoggedOut,
	useLoggedOutConfluence,
	useLoggedOutInIndiaForJira,
	useLoggedOutJiraFreeIntent,
	useLoggedInHasJiraSiteD1toD14,
	useExcludeJiraImcCampaign,
	useNewUser,
	useSpecifiedPathnames,
} from '@/components/user-context/helpers/cohorts';

import { Manifest } from './use-experiment';
import {
	wacRefreshConfluence,
	wacRefreshHomepage,
	wacRefreshJira,
	wacRefreshTwcRovo,
} from './wac-refresh-experiments';

const booleanTypeGuard = (value: unknown): value is boolean => typeof value === 'boolean';

export const experiments = {
	gwp9322WacHomepageRefresh: {
		experimentName: 'gwp-9322-wac-homepage-refresh-experiment',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Teamwork Collection refresh changes on homepage',
			expectedReleaseDate: '01/15/2026',
			expectedRuntime: 'indefinite',
			context: {
				urls: ['/'],
			},
		},
	},
	// GWP-12151: A/B/C test of the 1mind.ai "superhuman" AI chat vs the current
	// Omnichannel widget on the JPD product-discovery page. Three equal cells:
	//   - control:  current experience (Omnichannel)
	//   - ai_guide: 1mind "AI Guide" persona (waveform)  -> deployment-rprgpn5ltf
	//   - stephen:  1mind "Stephen" persona (human)       -> deployment-fhn85kw52t
	// NOTE: `experimentName`, `parameterName` and the three cell values below MUST
	// match the Statsig console experiment exactly, or getExperimentValue returns
	// undefined and every user silently falls back to `control`.
	gwp12151Superhuman1mind: {
		experimentName: 'gwp-12151-1mind-superhuman',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'ai_guide' | 'stephen',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'ai_guide' | 'stephen' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'ai_guide', 'stephen'].includes(value);
			},
		},
		targeting: {
			cohort: () => {
				// oxlint-disable-next-line react-hooks/rules-of-hooks
				return useSpecifiedPathnames([
					'/software/jira/product-discovery',
					'/software/jira/product-discovery/pricing',
					'/agile/product-management',
				]);
			},
		},
		metadata: {
			description:
				'A/B/C test of the 1mind superhuman AI chat (AI Guide / Stephen personas) vs the Omnichannel widget on the JPD product-discovery page',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '9 weeks',
			context: {
				urls: [
					'/software/jira/product-discovery',
					'/software/jira/product-discovery/pricing',
					'/agile/product-management',
				],
			},
		},
	},
	// TWCG-2108: Loom Land opt-in choice on /try/cloud/loom/choose (the signup
	// funnel step before /try/cloud/signup). Three cells; exposure is deferred
	// (`fireExperimentExposure: false`) so it only counts users who actually
	// reach the choice surface (fired via useLoomLandOptIn().fireExposure()):
	//   - control:  fall through to today's Loom-only signup flow
	//   - radio:    multi-option "TWC choice" card (LoomChoiceVariantA)
	//   - longText: single-option TWC pitch (LoomChoiceVariantB)
	// NOTE: `experimentName`, `parameterName` (`cohort`) and the three cell values
	// below MUST match the Statsig/Switcheroo console experiment exactly, or
	// getExperimentValue returns the defaultValue and every user silently falls
	// back to `control`.
	twcg2108LoomLandOptIn: {
		experimentName: 'twcg-2108-loom-land-opt-in',
		parameterName: 'cohort',
		defaultValue: 'control' as 'control' | 'radio' | 'longText',
		options: {
			fireExperimentExposure: false,
			typeGuard: (value: unknown): value is 'control' | 'radio' | 'longText' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'radio', 'longText'].includes(value);
			},
		},
		targeting: {
			// Only evaluate on the two surfaces this experiment cares about: the
			// signup funnel (where exposure fires post-identity on the logged-in
			// site-creation screen — see use-account-aware-loom-exposure.ts) and
			// the choose route (where the variant UI renders). Loom scoping is
			// already guaranteed downstream: exposure only fires from the Loom
			// logged-in new-user surface, so no product-intent gate is needed here.
			// oxlint-disable-next-line react-hooks/rules-of-hooks
			cohort: () => useSpecifiedPathnames(['/try/cloud/signup', '/try/cloud/loom/choose']),
		},
		metadata: {
			description:
				'Loom Land opt-in: offer a Teamwork Collection choice surface (radio/longText) vs the current Loom-only signup (control). Scoped to /try/cloud/signup (exposure fires post-identity on the logged-in Loom site-creation screen) and /try/cloud/loom/choose (variant renders here); Loom scoping is guaranteed by the exposure surface.',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '6 weeks',
			context: {
				urls: ['/try/cloud/signup', '/try/cloud/loom/choose'],
			},
		},
	},
	cfind4782LandingPageSubAnchorV3: {
		experimentName: 'cfind-4782-landing-page-sub-anchor-v3',
		parameterName: 'enrolled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: (value: unknown): value is boolean => typeof value === 'boolean',
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Test if personalizing the landing page off the sub anchor will increase d1to6',
			expectedReleaseDate: '07/14/2025',
			expectedRuntime: '6 weeks',
			// TODO: GWP-5270 to fill these urls
			context: {
				urls: [],
			},
		},
	},
	signupAddPaymentAb: {
		experimentName: 'signup_trialing_add_payment_ab',
		parameterName: 'cohort',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			// we need to fire our exposure event manually because we can't determine if the user is eligible from here
			fireExperimentExposure: false,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Experiment to enable users with valid sites to go to payment at the sign-up screen',
			expectedReleaseDate: '02/27/25',
			expectedRuntime: 'TBD',
			// TODO: GWP-5270 to fill these urls
			context: {
				urls: [],
			},
		},
	},
	gwp5090ExperimentBannerConfluence: {
		experimentName: 'gwp-5090-experiment-banner-confluence',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'butterbar' | 'lifted',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'butterbar' | 'lifted' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'butterbar', 'lifted'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Testing butterbar vs lifted banner for Jira Service Management product tour',
			expectedReleaseDate: '02/18/25',
			expectedRuntime: '2 weeks',
			// TODO: GWP-5270 to fill these urls
			context: {
				urls: [],
			},
		},
	},
	gwp7617JiraShortTermAiInfusionExperiment: {
		experimentName: 'gwp-7617-jira-short-term-ai-infusion-experiment',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Short term AI',
			expectedReleaseDate: '08/21/25',
			expectedRuntime: 'indefinite',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	gwp4948GartnerJiraProductTourExperiment: {
		experimentName: 'gwp-4948-gartner-jira-product-tour-experiment',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'gartner section on jira product tour',
			expectedReleaseDate: 'April 2025',
			expectedRuntime: '2 weeks',
			clientOnly: true,
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	gwp6441ConfluenceTemplatesProductNavCta: {
		experimentName: 'gwp-6441-confluence-templates-product-nav-cta',
		parameterName: 'variant',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => {
				// oxlint-disable-next-line react-hooks/rules-of-hooks
				return !useLoggedOut();
			},
		},
		metadata: {
			description: 'Confluence Templates Product Nav CTA experiment',
			expectedReleaseDate: 'undefined',
			expectedRuntime: '77 days',
			clientOnly: true,
			context: {
				urls: ['/software/confluence/templates/*'],
			},
		},
	},
	twcg2273CustomersPagePrimaryCta: {
		experimentName: 'twcg-2273-customers-page-primary-cta',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedOut,
		},
		metadata: {
			description: 'Customers page primary CTA click-through experiment',
			expectedReleaseDate: '07/29/2026',
			expectedRuntime: '28 days',
			clientOnly: true,
			context: {
				urls: ['/customers'],
			},
		},
	},
	recommendWorkEmailSignup: {
		experimentName: 'spark_recommend_work_email_signup',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			// Exposure is fired manually (via fireExposure) only once the user has
			// entered a valid personal-domain email — i.e. the eligible population —
			// rather than automatically for every visitor on page load. This keeps
			// the control/treatment arms comparable and avoids diluting the sample
			// with users who never reach the point where the nudge could show.
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			// Logged-out visitors in the Jira free signup flow only. The nudge
			// component lives on the signup-page InputSubmitButton that the Jira
			// free/standard/premium logged-out pages render; targeting is scoped to
			// Jira free per the experiment plan.
			cohort: useLoggedOutJiraFreeIntent,
		},
		metadata: {
			description:
				'Nudge users who enter a personal-domain email in the try/cloud signup form to use a work email (TWCG-2324)',
			expectedReleaseDate: '08/26/2026',
			expectedRuntime: '28 days',
			clientOnly: true,
			context: {
				urls: ['/try/cloud/signup'],
			},
		},
	},
	wacRoutingTableMvpPerf: {
		experimentName: 'wac-routing-table-mvp',
		parameterName: 'enrolled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is boolean => typeof value === 'boolean',
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Measure performance degredation of wac-routing-table in wac-web',
			expectedReleaseDate: '08/28/2025',
			expectedRuntime: '5 days',
			context: {
				urls: ['/software/jira/enterprise'],
			},
		},
	},
	confluencePricingPageWaeMigration: {
		experimentName: 'confluence_pricing_wae_migration_exp_rerun_exp_1',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Migration of Confluence pricing page to WAE, rerun',
			expectedReleaseDate: '03/06/2026',
			expectedRuntime: '80 days',
			context: {
				urls: ['/software/confluence/pricing'],
			},
		},
	},
	gwp8288PersonalizingWacSoftwarePage: {
		experimentName: 'gwp-8288-personalizing-wac-software-page',
		parameterName: 'enrolled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Personalizing WAC Software Page Experiment',
			expectedReleaseDate: '10/30/2025',
			expectedRuntime: '6 weeks',
			context: {
				urls: ['/software'],
			},
		},
	},
	twcg790ContextualBackground: {
		experimentName: 'twcg-790-contextual-backgrounds-wac-sign-in',
		parameterName: 'cohort',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'adding atlOrigin and application query strings to IDAC login URL to enable contextual background on sign in page',
			expectedReleaseDate: '01/12/2025',
			expectedRuntime: '7 weeks',
			clientOnly: true,
			context: {
				// eslint-disable-next-line
				urls: ['*'],
			},
		},
	},
	jiraPricingWaeMigrationExperimentRerunExp: {
		experimentName: 'jira_pricing_wae_migration_experiment_rerun_exp_1',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Rerun of migration of Jira pricing page to WAE',
			expectedReleaseDate: '03/06/2026',
			expectedRuntime: '43 days',
			context: {
				urls: ['/software/jira/pricing'],
			},
		},
	},
	dxpMigrationConfluenceAnchorJourney: {
		experimentName: 'dxp_migration_confluence_anchor_journey',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Migration of Confluence anchor journey to DXP',
			expectedReleaseDate: 'TBD',
			expectedRuntime: 'TBD',
			context: {
				urls: ['/software/confluence'],
			},
		},
	},
	cfind6214WacLandingForAiNativeTeamsPt: {
		experimentName: 'cfind-6214-wac-landing-for-ai-native-teams-pt',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => {
				return (
					// oxlint-disable-next-line react-hooks/rules-of-hooks
					useLoggedOut() &&
					// oxlint-disable-next-line react-hooks/rules-of-hooks
					useExcludeJiraImcCampaign()
				);
			},
		},
		metadata: {
			description:
				'Split /software/jira traffic for logged-out PT users (excluding the Jira IMC paid campaign); treatment serves the WAE (Uniform) landing page.',
			expectedReleaseDate: '06/30/2026',
			expectedRuntime: 'TBD',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	d1d6342LocaleBasedExpForLoggedOutUser: {
		experimentName: 'd1d6-342-locale-based-exp-for-logged-out-user',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedOutInIndiaForJira,
		},
		metadata: {
			description:
				'Locale-based experiment for logged-out users in India on the Jira software landing page (D1D6-342).',
			expectedReleaseDate: '08/21/2026',
			expectedRuntime: '1 month',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	jiraWacActivationChecklist: {
		experimentName: 'wac-jira-activation-checklist',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedInHasJiraSiteD1toD14,
		},
		metadata: {
			description:
				"Per-product activation checklist surfaced on wac.com/software/jira, rendered directly below the user's most recently accessed Jira site card. Hosts the @post-office/placements--growth-starburst/wac-product-activation-checklist placement.",
			expectedReleaseDate: '06/01/2026',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	gwp10724JiraAgentHeroChangesOnJiraPt: {
		experimentName: 'gwp-10724-jira-agent-hero-changes-on-jira-pt',
		parameterName: 'isEnabled',
		defaultValue: true,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Post-rollout retroactive experiment for visual changes above the fold on the Jira PT, related to Jira Agent release',
			expectedReleaseDate: '2026-03-18',
			expectedRuntime: '24 days',
			clientOnly: false,
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	wacRefreshQ4Fy26AaTest: {
		experimentName: 'wac_refresh_q4fy26_aa_test',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'AA test for WAC Refresh Q4 FY26',
			expectedReleaseDate: 'Team26',
			expectedRuntime: 'TBD',
			context: {
				urls: [
					'/',
					'/software/jira',
					'/software/confluence',
					'/software/rovo',
					'/collections/teamwork',
				],
			},
		},
	},
	loomLoggedInSignupParity: {
		experimentName: 'loom_logged_in_signup_parity',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Loom logged-in signup: render the lofi Loom-backdrop form (matching logged-out) instead of the default signup for logged-in users on ?bundle=loom',
			expectedReleaseDate: '07/28/2026',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/try/cloud/signup?bundle=loom'],
			},
		},
	},
	sparkHdyhauSignupExperiment: {
		experimentName: 'spark-hdyhau-signup-experiment',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useNewUser,
		},
		metadata: {
			description: 'HDYHAU signup experiment',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '24 days',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	sparkDeferSiteNameV2: {
		experimentName: 'spark-defer-site-name-v2',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			// Must match the cohort === 'new-user' branch in logged-in-jira-free.tsx
			// (useCohort(true)). Do not use useNewUser here — that is a different check.
			cohort: useLoggedInNewUser,
		},
		metadata: {
			description: 'Skip the create-site step for Jira free new users',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '33 days',
			context: {
				urls: ['/try/cloud/signup'],
			},
		},
	},
	twcg1935CodingAgentChoice: {
		experimentName: 'spice-agentic-development-onboarding',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useEligibleForTwcg1935CodingAgentChoice,
		},
		metadata: {
			description: 'Show coding agent choice screen during Jira onboarding',
			expectedReleaseDate: '06/29/2026',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	cfindTemplatesBlendedHeroV7: {
		experimentName: 'cfind_templates_blended_hero_v7',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Blended Hero on Confluence templates page — split hero retaining the template search bar (left) alongside a marketing/setup card with value headline, setup steps, and CTAs (right)',
			expectedReleaseDate: '07/28/2026',
			expectedRuntime: '5 weeks',
			context: {
				urls: ['/software/confluence/templates'],
			},
		},
	},
	jsmContextualBanditsInWae: {
		experimentName: 'jsm-contextual-bandits-in-wae',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Contextual bandits in WAE on the JSM product page',
			expectedReleaseDate: '06/25/2026',
			expectedRuntime: 'indefinite',
			context: {
				urls: ['/software/jira/service-management'],
			},
		},
	},
	jsmgrowth535PreviewPlayground: {
		experimentName: 'jsmgrowth_535_preview_playground',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'New JSM product tour page with advanced preview playground',
			expectedReleaseDate: '10/25/2026',
			expectedRuntime: '30 days',
			context: {
				urls: ['/software/jira/service-management'],
			},
		},
	},
	confluenceContextualBanditsInWae: {
		experimentName: 'confluence-contextual-bandits-in-wae',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Contextual bandits in WAE on the Confluence anchor journey landing pages — personalises above-the-fold headline, subheadline and supporting copy per search/ad intent (GWP-12850, TWCG-2162)',
			expectedReleaseDate: '08/21/2026',
			expectedRuntime: 'indefinite',
			context: {
				urls: ['/software/confluence'],
			},
		},
	},
	jsmGrowth519JsmLandUseCaseOnboarding: {
		experimentName: 'jsmgrowth_519_jsm_land_use_case_onboarding',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			// Exposure is fired only when the Team Choice screen is viewed.
			// Other reads of this experiment must not count exposure.
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useEligibleForJsmLandUseCaseOnboarding,
		},
		metadata: {
			description:
				'JSM Land use-case driven onboarding — reorders the team choice screen and adds expand/collapse options for JSM new user signups',
			expectedReleaseDate: '07/06/2026',
			expectedRuntime: '8 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	jfindOnboardIllustrationRefresh: {
		experimentName: 'jfind-onboard-illustration-refresh',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Replace the static SVG on the right side of the Jira onboarding wizard with a refreshed illustration component (TWCG-1861)',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	team26EuConfluencePt: {
		experimentName: 'team_26_eu_confluence_pt',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedOutConfluence,
		},
		metadata: {
			description:
				'Test updated copy and assets on the WAC Confluence product tour following the Team 26 refresh',
			expectedReleaseDate: 'TBD',
			expectedRuntime: 'TBD',
			context: {
				urls: ['/software/confluence'],
			},
		},
	},
	wacSoftwareAATest: {
		experimentName: 'wac-software-a-a',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'AA test for WAC Software',
			expectedReleaseDate: '09/02/2026',
			expectedRuntime: '2 weeks',
			context: {
				urls: ['/software'],
			},
		},
	},
	wacSoftwareJiraAATest: {
		experimentName: 'wac-software-jira-a-a',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'AA test for WAC Software Jira',
			expectedReleaseDate: '09/09/2026',
			expectedRuntime: '2 weeks',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	wacSearchEnSignupCta: {
		experimentName: 'wac_search_en_signup_cta',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Contextual signup CTA card inline with search results on /search for EN locale',
			expectedReleaseDate: '08/07/2026',
			expectedRuntime: '6 weeks',
			context: {
				urls: ['/search'],
			},
		},
	},
	// Replaces the provisioning-step loading screen with the Loom onboarding steps
	expLoomOnboardingOnWac: {
		experimentName: 'exp-loom-onboarding-on-wac',
		// `cohort` matches the Statsig parameter key configured on the
		// console side (same convention as `signupAddPaymentAb` above).
		parameterName: 'cohort',
		defaultValue: 'control' as 'control' | 'variant-1',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'variant-1' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'variant-1'].includes(value);
			},
		},
		targeting: {
			cohort: useIsDesktop,
		},
		metadata: {
			description:
				"Replace the provisioning-step loading screen with the Loom onboarding screens on WAC's confirmation page to reduce perceived wait time for new users. Desktop only — mobile/tablet users are excluded from the cohort. Variant 1 show the onboarding UI; the variant name is available to the view for per-cell tweaks.",
			expectedReleaseDate: '10/2/2026',
			expectedRuntime: '2 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	twcg1930TwcWacHomepage: {
		experimentName: 'twcg-1930-twc-wac-homepage',
		parameterName: 'enrolled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Teamwork Collection experience on the WAC homepage (TWCG-1930)',
			expectedReleaseDate: '09/02/2026',
			expectedRuntime: '6 weeks',
			clientOnly: true,
			context: {
				urls: [],
			},
		},
	},
	team26EuTwcPt: {
		experimentName: 'team-26-eu-twc-pt',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => {
				// oxlint-disable-next-line react-hooks/rules-of-hooks
				return useSpecifiedPathnames(['/collections/teamwork']);
			},
		},
		metadata: {
			description:
				'TEAM 26 EU — 50/50 do-no-harm A/B of the copy & asset refresh on the Teamwork Collection product tour (/collections/teamwork). JPD: WEBPLAN-291.',
			expectedReleaseDate: 'TBD',
			expectedRuntime: 'TBD',
			context: {
				urls: ['/collections/teamwork'],
			},
		},
	},
	cfindWacCustomersTryItFreeV1: {
		experimentName: 'cfind_wac_customers_try_it_free_v1',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			// Target every visitor, logged in and logged out alike.
			//
			// This deliberately avoids useLoggedOut(). That hook answers "is this
			// visitor logged out?" from a different source on each side of hydration:
			// the httpOnly cloud session token cookie during SSR, then the
			// login_user_detected cookie on the client, then the resolved user profile
			// once the request lands. Those three can disagree, so cohort membership
			// could flip mid-render — the server rendering the CTA while the first
			// client render dropped back to the control, then flipping again once the
			// profile resolved. A constant cohort cannot diverge across hydration.
			//
			// The pathname guard is redundant: CarouselSection is the only production
			// caller that passes this experiment to CustomerTestimonials, and it only
			// renders on /customers (recorded in metadata.context.urls below).
			cohort: () => true,
		},
		metadata: {
			description:
				"Add a secondary inline 'Try it free' text-link CTA to each featured customer story card on the WAC Customers Hub, deep-linking to /try/cloud/signup?bundle=jira-software&edition=free. Targets all visitors, logged in and logged out.",
			expectedReleaseDate: '08/21/2026',
			expectedRuntime: '5 weeks',
			context: {
				urls: ['/customers'],
			},
		},
	},
	team26EuJiraPt: {
		experimentName: 'team_26_eu_jira_pt',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			// Logged-out visitors on the Jira product tour only. Control = current
			// copy & assets; treatment = TEAM 26 EU refreshed copy & assets.
			cohort: useLoggedOut,
		},
		metadata: {
			description:
				'TEAM 26 EU do-no-harm A/B on the Jira product tour (/software/jira): refreshed copy & assets vs current experience.',
			expectedReleaseDate: '10/12/2026',
			expectedRuntime: '28 days',
			clientOnly: true,
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	team26EuHomepage: {
		experimentName: 'team_26_eu_homepage',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			// Logged-out visitors on the homepage only. Control = current copy &
			// assets; treatment = TEAM 26 EU refreshed copy & assets.
			cohort: useLoggedOut,
		},
		metadata: {
			description:
				'TEAM 26 EU do-no-harm A/B on the homepage (/): refreshed copy & assets vs current experience.',
			expectedReleaseDate: '10/12/2026',
			expectedRuntime: '28 days',
			clientOnly: true,
			context: {
				urls: ['/'],
			},
		},
	},
	// TWCG-2490 / FD-232121: show the global-nav "Contact us" CTA to logged-in
	// users whose GUPS account segment is Enterprise or Strategic, on the WAC
	// homepage only, desktop only.
	//
	// NOTE: `experimentName` and `parameterName` MUST match the Switcheroo /
	// Statsig experiment exactly, or getExperimentValue returns undefined and
	// every user silently falls back to `false`.
	twcg2490EnterpriseContactSalesCta: {
		experimentName: 'wac_enterprise_contact_sales_cta',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			// Exposure is fired MANUALLY by useEnterpriseContactSalesCtaEligibility,
			// and only once the GUPS account-segment lookup resolves to an
			// eligible segment. Enrolling every homepage visitor instead would
			// dilute the enrolled population from ~6,483 to ~71,358 per 4 weeks
			// (~11x) and collapse the MDE. Do not set this to true.
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => {
				// Route scoping lives here rather than in the shared
				// `wac-sales-leads-cta-route-list` dynamic config, which the
				// live SMB CTA also reads — narrowing that config would pull
				// the SMB CTA off the Jira and Confluence product tours.
				// oxlint-disable-next-line react-hooks/rules-of-hooks
				return useSpecifiedPathnames(['/']);
			},
		},
		metadata: {
			description:
				'Show the global-nav "Contact us" CTA to logged-in Enterprise/Strategic-segment users on the WAC homepage. Desktop only. Switcheroo FD-232121.',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '28 days',
			clientOnly: true,
			context: {
				urls: ['/'],
			},
		},
	},
	wacRefreshJira,
	wacRefreshConfluence,
	wacRefreshHomepage,
	wacRefreshTwcRovo,
} satisfies Manifest<unknown>;
